import { useState } from 'react';
import { hasProviderSyncErrors, PROVIDER_LABELS } from '@/lib/channels';
import type { ApartmentChannelMapping, Provider } from '@/lib/channels';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Tabs } from '@/components/ui/tabs';
import { ChannelBadge, type BookingChannel } from '@/components/ui/channel-badge';
import { SyncErrors } from '@/components/apartment/SyncErrors';
import { ConnectionTab } from './ConnectionTab';
import { SettingsTab } from './SettingsTab';

interface Props {
  provider: Provider;
  apartmentID: string;
  mapping: ApartmentChannelMapping | undefined;
  canWrite: boolean;
}

// Maps our provider codes to the ChannelBadge brand identities.
const PROVIDER_CHANNELS: Partial<Record<Provider, BookingChannel>> = {
  booking_com: 'booking',
  airbnb: 'airbnb',
};

function StatusBadge({ mapping }: { mapping: ApartmentChannelMapping | undefined }) {
  if (!mapping) {
    return (
      <Badge variant="neutral" size="sm">
        Not configured
      </Badge>
    );
  }
  if (mapping.sync_status === 'error') {
    return (
      <Badge variant="danger" size="sm">
        Error
      </Badge>
    );
  }
  if (mapping.enabled) {
    return (
      <Badge variant="success" size="sm" dot>
        Active
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" size="sm">
      Inactive
    </Badge>
  );
}

const DIALOG_TABS = [
  { value: 'connection', label: 'Connection' },
  { value: 'settings', label: 'Settings' },
];

export function ChannelCard({ provider, apartmentID, mapping, canWrite }: Props) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('connection');

  // Count of this provider's currently-active errors (not superseded by an
  // acknowledge, a later manual sync, or a later ARI push) — sourced from
  // the channels-list response, which is the only response that computes
  // active_error_count (see ApartmentChannelMapping's doc comment). No time
  // window/dismiss problem here: unlike the old client-side count of every
  // severity=error row ever recorded, this only ever counts rows still
  // actually masking something.
  const errorCount = mapping?.active_error_count ?? 0;

  const channel = PROVIDER_CHANNELS[provider];
  const enabled = mapping?.enabled ?? false;

  return (
    <>
      <div
        className={`rounded-[var(--radius-control)] border p-4 ${
          enabled
            ? 'border-[var(--border-hairline)] bg-card'
            : 'border-[var(--border-soft)] bg-[var(--surface-sunken)]'
        }`}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          {channel ? (
            <ChannelBadge channel={channel} bare />
          ) : (
            <h3 className="text-sm font-semibold text-foreground">
              {PROVIDER_LABELS[provider]}
            </h3>
          )}
          <StatusBadge mapping={mapping} />
        </div>

        {/* Only on a card that also renders the list below, so the count is
            always something the operator can click into. A card with no list
            (see the gate further down) would otherwise be able to announce
            "1 active error" with nowhere to open it — those rows are surfaced
            by the apartment-wide "Other sync errors" block instead, which
            carries its own rows and needs no count here. */}
        {hasProviderSyncErrors(provider) && errorCount > 0 && (
          <p className="mb-3 text-xs text-destructive">
            {errorCount} active error{errorCount !== 1 ? 's' : ''}
          </p>
        )}

        {mapping && (
          <dl className="mb-3 space-y-1 text-xs">
            {mapping.last_synced_at && (
              <div className="flex items-baseline gap-2">
                <dt className="min-w-[5rem] text-muted">Last synced</dt>
                <dd className="flex-1 truncate">
                  {new Date(mapping.last_synced_at).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        )}

        {/* Only OTA cards carry a sync-error list; booking_engine can never
            have an event, so don't spend a request finding that out. Anything
            stored against it still shows up in the apartment-wide "Other sync
            errors" block below the cards. */}
        {hasProviderSyncErrors(provider) && (
          <SyncErrors apartmentID={apartmentID} provider={provider} />
        )}

        {canWrite && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setActiveTab('connection');
              setOpen(true);
            }}
          >
            {mapping ? 'Configure' : 'Set up'}
          </Button>
        )}
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={PROVIDER_LABELS[provider]}
        description={mapping ? undefined : 'Not yet configured'}
        side
      >
        <div className="flex h-full flex-col gap-5">
          <Tabs
            tabs={DIALOG_TABS}
            value={activeTab}
            onChange={setActiveTab}
          />
          <div className="flex-1">
            {activeTab === 'connection' && (
              <ConnectionTab
                apartmentID={apartmentID}
                provider={provider}
                mapping={mapping}
                canWrite={canWrite}
              />
            )}
            {activeTab === 'settings' && (
              <SettingsTab
                apartmentID={apartmentID}
                provider={provider}
                mapping={mapping}
                canWrite={canWrite}
              />
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
